<template>
  <div class="pt-1.5 pr-1.5 pb-1.5 flex flex-col overflow-hidden h-full" :class="defaultColorClass">
    <MassSignModal v-model="isMassSignOpen" v-if="isMassSignEnabled" @close="onMassSignClose">
      <template #activator>
        <div></div>
      </template>
    </MassSignModal>
    <div class="bg-white flex flex-col lg:h-0 grow overflow-hidden">
      <div class="hidden lg:flex gap-1 xl:gap-2 items-center px-2 xl:px-3 pt-[10px] pb-[11px]">
        <h2 class="font-bold text-h5 xl:text-h4">Documents</h2>
        <PDropdown :same-width="false" :delay="500" :offset="5" class="mr-auto">
          <template #activator="{ attrs, evtHandlers }">
            <PButton
              v-bind="attrs"
              v-on="evtHandlers"
              size="icon"
              mode="tertiary-borderless"
              aria-label="Voir le détail du nombre de documents"
            >
              <OhVueIcon :name="infoIcon" class="text-info-200 mt-1v" />
            </PButton>
          </template>
          <template #default>
            <div class="p-2">
              <span class="font-bold">Total : </span>
              <span v-if="nbLinkDocs">{{ nbLinkDocs }} {{ plural("lien", nbLinkDocs) }} et </span>
              <span>
                {{ nbDefaultDocs }}
                {{ plural("document", nbDefaultDocs) }} dans {{ folders.length }}
                {{ plural("dossier", folders.length) }}
              </span>
              <ul class="mt-1v">
                <li v-for="folder in folders" :key="folder.id">
                  <FolderContentSummary :folder="folder" />
                </li>
                <li v-if="trashFolder">Corbeille : {{ trashFolder.documents?.length || 0 }}</li>
              </ul>
            </div>
          </template>
        </PDropdown>
        <PDropdown v-if="!readonly" :same-width="false" :offset="5" placement="bottom" class="mr-1v">
          <template #activator="{ toggle, isOpen }">
            <PButton
              @click="toggle"
              mode="secondary"
              :aria-expanded="isOpen"
              :data-sentry-id="`DocsAddToggle-${isOpen}`"
            >
              Ajouter
              <OhVueIcon :name="arrowDownIcon" class="ml-1" />
            </PButton>
          </template>
          <template #default="{ close }">
            <div class="px-2 py-1 flex flex-col gap-1 text-grey-50 text-sm">
              <button @click="addFolder(close)" class="text-start" data-sentry-id="DocsAddFolderBtn">
                Un classeur
              </button>
              <button
                @click="addDocument(rootFolderId, close)"
                :disabled="isUploading"
                class="text-start border-t pt-1v border-grey-925 disabled:text-grey-625 disabled:cursor-not-allowed"
                data-sentry-id="DocsAddDocBtn"
              >
                Document(s)
              </button>
              <DocLinkForm
                v-if="rootFolderId"
                label="Un lien"
                :root-folder-id="rootFolderId"
                v-model="isDocLinkFormOpened"
              />
              <button @click="isDocLinkFormOpened = true" class="text-start">Un lien</button>
            </div>
          </template>
        </PDropdown>
        <FileLinkLoader v-if="letterFile" :id="letterFile.id" type="letterFile" v-model="isFileLinkOpened1" />
        <FileLinkLoader v-if="letterFile" :id="letterFile.id" type="letterFile" v-model="isFileLinkOpened2" mailto />
        <KebabMenu placement="bottom-end" class="pb-1v" aria-label="Action rapide sur les documents">
          <template #default="{ close }">
            <button
              v-if="letterFile"
              @click="signWholeLetterFile"
              class="text-start font-bold"
              data-sentry-id="SignWholeLfBtn"
              :disabled="letterFile.signaturePositionsCount <= 0 || readonly"
              :class="{
                'text-grey-625 cursor-not-allowed': letterFile.signaturePositionsCount <= 0 || readonly,
              }"
            >
              Signer le parapheur
            </button>
            <button
              v-if="letterFile"
              @click="isFileLinkOpened1 = true"
              class="text-start"
              data-sentry-id="FileLinkLoaderDlBtn"
              :disabled="!hasDocuments"
              :class="{ 'text-grey-625 cursor-not-allowed': !hasDocuments }"
            >
              Télécharger
            </button>
            <button
              v-if="letterFile"
              @click="isFileLinkOpened2 = true"
              class="text-start"
              data-sentry-id="FileLinkLoaderDlBtn"
              :disabled="!hasDocuments"
              :class="{ 'text-grey-625 cursor-not-allowed': !hasDocuments }"
            >
              Envoyer par mail
            </button>
            <PTooltip
              label="Action impossible sur un parapheur désarchivé"
              :disabled="canEdit || !hasDocuments"
              :delay="500"
              :offset="5"
            >
              <button
                v-if="!readonly"
                :disabled="!canEdit || !hasDocuments"
                :class="{
                  'text-grey-625 cursor-not-allowed': !canEdit || !hasDocuments,
                  'text-error-425': canEdit && hasDocuments,
                }"
                @click="removeAllDocs(close)"
                class="border-t pt-1v border-grey-925 text-start"
                data-sentry-id="DocsDeleteAllBtn"
              >
                Tout supprimer
              </button>
            </PTooltip>
            <button
              v-if="letterFile"
              class="text-start border-t pt-1v border-grey-925 flex items-center gap-1v"
              data-sentry-id="TrashBtn"
              :disabled="isTrashEmpty"
              :class="{ 'text-grey-625 cursor-not-allowed': isTrashEmpty }"
              @click="isTrashOpened = true"
            >
              <OhVueIcon :name="trashIcon" />
              <span> Corbeille ({{ trashDocuments.length }}) </span>
            </button>
          </template>
        </KebabMenu>
      </div>
      <Progressbar v-if="isUploading" class="mt-2 mx-2 xl:mx-4" role="status" aria-label="Téléversement en cours" />
      <div class="flex flex-col overflow-hidden mb-1 mt-1v lg:h-0 grow overflow-y-auto will-change-scroll">
        <div class="flex flex-col justify-start h-full">
          <DraggableList
            v-if="folders.length > 1 || (folders[0]?.documents || []).length > 0"
            group-name="folders"
            data-id-attr="data-folder-id"
            @drop="reorderFolders"
            @choose="draggedFolderIndex = $event.oldIndex!"
            @un-choose="draggedFolderIndex = null"
            handle="svg.drag-handle"
          >
            <Folder
              v-for="(folder, i) in folders"
              :key="folder.id"
              :folder="folder"
              :letter-file="letterFile"
              :readonly="readonly"
              :is-uploading="isUploading"
              :is-first="i === 0"
              :is-last="i >= folders.length - 1"
              @add-document="addDocument(folder.id)"
              @upload-files="uploadFiles($event, folder.id)"
              @move="moveFolder(folder, $event)"
              @move-doc="moveToFolder($event, i)"
              @edit-doc="editDoc"
              class="py-1"
              :class="{
                'bg-blue-cumulus-975!': draggedFolderIndex === i,
              }"
            >
            </Folder>
          </DraggableList>
          <Dropfile
            v-if="!readonly"
            @input="uploadFilesFromDrop"
            :disabled="isUploading"
            class="h-full"
            :class="{
              'py-4': !dragging && (hasDocuments || folders.length > 1),
            }"
            @dragging="dragging = $event"
          >
            <div
              v-if="dragging || (!hasDocuments && folders.length === 1)"
              class="m-2 h-full border-dotted rounded-md border-grey-925 border-2 text-grey-425"
            >
              <div class="flex flex-col items-center justify-center w-full h-full px-2" id="drop-hover">
                <template v-if="isUploading">
                  <span> Téléversement en cours… </span>
                  <span class="text-sm">Vous pourrez déposer d'autres documents à la fin du téléversement</span>
                </template>
                <template v-else>
                  <span> Glissez et déposez vos documents </span>
                  <p class="line-clamp-3">
                    <span class="text-sm">Destination : </span>
                    <span class="italic text-sm">{{ getFolderName(folders.at(-1)!) }}</span>
                  </p>
                  <span class="text-sm">
                    Taille maximum par document:
                    {{ config.fileSizeLimit.replace("i", "").replaceAll("B", "o") }}
                  </span>
                </template>
              </div>
            </div>
          </Dropfile>
          <div
            v-else-if="!!letterFile?.folders?.length"
            class="mx-2 mt-2 mb-1 h-full flex items-center justify-center italic"
          >
            <span class="text-grey-425">Vous ne pouvez pas ajouter de documents pour le moment</span>
          </div>
          <div v-else class="m-4 h-full flex flex-col animate-pulse gap-2">
            <div
              v-for="i in 5"
              :key="i"
              class="border border-grey-925 flex items-center pl-2 py-1 gap-2 bg-white shadow-inner h-7 w-full"
            >
              <div class="bg-grey-900 h-4 w-4"></div>
              <div class="bg-grey-900 h-3 w-2/3"></div>
            </div>
          </div>
        </div>
        <div class="lg:hidden pb-[100px]"></div>
      </div>
      <FileUpload ref="fileUpload" :multiple="true" @uploaded="createDocuments($event, addingDocumentToFolderId!)" />
    </div>
    <!-- wopi (collabora) form -->
    <div class="hidden" v-if="wopiStore.enabled">
      <form
        ref="wopiForm"
        :action="wopiStore.currentUrl || ''"
        enctype="multipart/form-data"
        method="post"
        id="wopi-submit-form"
        target="wopi-online-viewer"
      >
        <input name="access_token" :value="wopiStore.currentToken" type="hidden" id="access-token" />
        <input type="submit" value="" />
      </form>
    </div>
    <Trash v-model="isTrashOpened" :documents="trashDocuments" />
  </div>
</template>

<script setup lang="ts">
import { FormOperation, type ParapheurConfig } from "@/app/types";
import {
  AlertDuration,
  AlertType,
  PButton,
  PDropdown,
  PTooltip,
  useAlertManager,
  useConfig,
  useIcons,
} from "@pasta/box-v2";
import { clone, findIndex, flatten } from "lodash";
import { OhVueIcon } from "oh-vue-icons";
import { RiArrowDropDownFill, RiDeleteBinLine, RiInformationLine } from "oh-vue-icons/icons";
import { type SortableEvent } from "sortablejs";
import { computed, inject, onUnmounted, ref } from "vue";
import DraggableList from "../../../components/DraggableList.vue";
import FileLinkLoader from "../../../components/FileLinkLoader.vue";
import KebabMenu from "../../../components/KebabMenu.vue";
import MassSignModal from "../../../components/MassSignModal.vue";
import Progressbar from "../../../components/Progressbar.vue";
import Trash from "../../../components/Trash.vue";
import { useMassSign } from "../../../composable/use-mass-sign";
import { useNotifyAsyncOperation } from "../../../composable/useAsyncOperation";
import { useLetterFileColor } from "../../../composable/useLetterFileColor";
import { ROOT_FOLDER_NAME, TRASH_FOLDER_NAME } from "../../../consts";
import { type LetterFileDocumentDto, LetterFileDocumentType } from "../../../dto/letter-file-document.dto";
import { type LetterFileFolderDto } from "../../../dto/letter-file-folder.dto";
import { useLetterFileDocumentApi } from "../../../http/letter-file-document.api";
import { useLetterFileFolderApi } from "../../../http/letter-file-folder.api";
import { useLetterFileStore } from "../../../stores/letter-file.store";
import { useWopiStore } from "../../../stores/wopi.store";
import { plural } from "../../../utils/plural";
import FileUpload from "../file-upload.vue";
import { canEditAfterArchival, type FormProvide, FormToken } from "../utils";
import DocLinkForm from "./DocLinkForm.vue";
import Dropfile from "./Dropfile.vue";
import Folder from "./Folder.vue";
import FolderContentSummary from "./FolderContentSummary.vue";
import { getFolderName } from "./utils";

interface Props {
  readonly: boolean;
}

defineProps<Props>();

const [arrowDownIcon, trashIcon, infoIcon] = useIcons(RiArrowDropDownFill, RiDeleteBinLine, RiInformationLine);
const letterFileStore = useLetterFileStore();
const folderApi = useLetterFileFolderApi();
const documentApi = useLetterFileDocumentApi();
const config = useConfig<ParapheurConfig>();
const alertManager = useAlertManager();
const notifyAsyncOperation = useNotifyAsyncOperation();
const { refresh, letterFile } = inject<FormProvide>(FormToken)!;
const { defaultColorClass } = useLetterFileColor();

const fileUpload = ref<InstanceType<typeof FileUpload> | null>(null);
const isDocLinkFormOpened = ref(false);
const isFileLinkOpened1 = ref(false);
const isFileLinkOpened2 = ref(false);

const folders = computed(() => {
  return (letterFile.value?.folders || []).filter(folder => folder.name !== TRASH_FOLDER_NAME);
});
const rootFolderId = computed(() => {
  const root = folders.value.find(f => f.name === ROOT_FOLDER_NAME)!;
  return root?.id;
});

const documents = computed(() => flatten(folders.value.map(folder => folder.documents || [])));

const nbDefaultDocs = computed(() => documents.value.filter(doc => doc.type === LetterFileDocumentType.DEFAULT).length);
const nbLinkDocs = computed(() => documents.value.filter(doc => doc.type === LetterFileDocumentType.URL).length);

const hasDocuments = computed(() => {
  return documents.value.length > 0;
});

const isTrashOpened = ref(false);
const trashFolder = computed(() => {
  return letterFile.value?.folders?.find(folder => folder.name === TRASH_FOLDER_NAME);
});
const trashDocuments = computed(() => {
  if (trashFolder.value) {
    return trashFolder.value.documents || [];
  }
  return [];
});
const isTrashEmpty = computed(() => {
  return !trashDocuments.value?.length;
});

const canEdit = computed(() => canEditAfterArchival(letterFile.value!));

async function addFolder(closeMenu: () => void) {
  const baseName = "Nouveau classeur";
  const regex = /^Nouveau classeur \((\d+)\)$/;
  const lastDigit = folders.value
    .map(folder => {
      const digit = folder.name.match(regex);
      if (digit) return parseInt(digit[1]);
      return 1;
    })
    .sort()
    .pop();

  await notifyAsyncOperation(async () => {
    await folderApi.create({
      name: (letterFile.value!.folders || []).length > 1 && lastDigit ? `${baseName} (${lastDigit + 1})` : baseName,
      letterFile: letterFile.value!.id,
    });
  }, FormOperation.FOLDER);
  await notifyAsyncOperation(refresh, FormOperation.REFRESH);
  closeMenu();
}

const creatingDocuments = ref<boolean>(false);
const isUploading = computed(() => creatingDocuments.value || !!fileUpload.value?.isUploading);

async function uploadFiles(files: File[], folderId: number) {
  if (isUploading.value) return;
  addingDocumentToFolderId.value = folderId;
  try {
    await fileUpload.value!.uploadFiles(files);
  } finally {
    addingDocumentToFolderId.value = null;
  }
}

async function createDocuments(uploads: { fileId: number; file: File }[], folderId: number) {
  creatingDocuments.value = true;
  try {
    await notifyAsyncOperation(async () => {
      for (const upload of uploads) {
        await createDocument(upload.fileId, upload.file, folderId);
      }
    }, FormOperation.DOCUMENT);
    await notifyAsyncOperation(refresh, FormOperation.REFRESH);
  } finally {
    creatingDocuments.value = false;
  }
}

async function createDocument(fileId: number, file: File, folderId: number) {
  await notifyAsyncOperation(async () => {
    await documentApi.create(
      {
        name: file.name,
        fileId,
        folder: folderId,
      },
      letterFile.value!.id
    );
  }, FormOperation.DOCUMENT);
}

const dragging = ref(false);

async function uploadFilesFromDrop(files: File[]) {
  await uploadFiles(files, folders.value.at(-1)?.id || rootFolderId.value);
}

const addingDocumentToFolderId = ref<number | null>(null);

function addDocument(folderId: number, closeFn?: () => void) {
  if (fileUpload.value) {
    addingDocumentToFolderId.value = folderId;
    fileUpload.value.open();
  }
  if (closeFn) closeFn();
}

const draggedFolderIndex = ref<number | null>(null);

async function reorderFolders(_event: SortableEvent, folderIds: number[]) {
  try {
    await notifyAsyncOperation(
      async () => letterFileStore.reorderFolders(letterFile.value!.id, folderIds),
      FormOperation.FOLDER
    );
  } catch {
    alertManager.createAlert({
      message: "Le classeur n'a pas pu être déplacé",
      type: AlertType.ERROR,
      duration: AlertDuration.MEDIUM,
    });
  } finally {
    await notifyAsyncOperation(refresh, FormOperation.REFRESH);
  }
}

async function moveFolder(folder: LetterFileFolderDto, direction: "up" | "down") {
  const cloned = clone(folders.value);
  const fromIndex = findIndex(cloned, folder);
  const toIndex = direction === "up" ? fromIndex - 1 : fromIndex + 1;
  const el = cloned.splice(fromIndex, 1)[0];
  cloned.splice(toIndex, 0, el);
  await reorderFolders(
    {} as SortableEvent,
    cloned.map(folder => folder.id)
  );
}

// Move one document to another folder
async function moveToFolder(
  moveArgs: { doc: LetterFileDocumentDto; direction: "up" | "down" },
  fromFolderIndex: number
) {
  const { doc, direction } = moveArgs;
  notifyAsyncOperation(async () => {
    const targetFolderIndex = direction === "up" ? fromFolderIndex - 1 : fromFolderIndex + 1;
    const targetFolder = letterFile.value!.folders!.at(targetFolderIndex)!;
    await documentApi.update(doc.id, letterFile.value!.id, {
      folder: targetFolder.id,
    });
    let docIds: number[];
    if (direction === "up") {
      docIds = [...(targetFolder.documents || []), doc].map(el => el.id);
    } else {
      docIds = [doc, ...(targetFolder.documents || [])].map(el => el.id);
    }
    await folderApi.reorder(targetFolder.id, letterFile.value!.id, docIds);
    await refresh();
  }, FormOperation.DOCUMENT);
}

async function removeAllDocs(close: () => void) {
  if (confirm("Êtes vous sûr(e) de vouloir supprimer tous les documents et tous les répertoires ?")) {
    try {
      await notifyAsyncOperation(
        async () => letterFileStore.removeAllDocs(letterFile.value!.id),
        FormOperation.DOCUMENT
      );
    } catch {
      alertManager.createAlert({
        message: "Une erreur s'est produite durant la suppression des documents",
        type: AlertType.ERROR,
        duration: AlertDuration.MEDIUM,
      });
    } finally {
      await notifyAsyncOperation(refresh, FormOperation.REFRESH);
      close();
    }
  } else {
    close();
  }
}

// edit files with collabora
const wopiStore = useWopiStore();
const wopiForm = ref<HTMLFormElement | null>(null);

async function editDoc(_id: number) {
  if (wopiForm.value) {
    wopiForm.value.submit();
  }
}

// Mass sign
const { isMassSignEnabled, isMassSignOpen, verifyHasSignatureObject, massSignatureStore } = useMassSign();

function signWholeLetterFile() {
  if (!verifyHasSignatureObject()) return;
  if (!massSignatureStore.currentMassSignature?.creationDate) {
    massSignatureStore.isolateLetterFile(letterFile.value!.id);
  }
  isMassSignOpen.value = true;
}

function onMassSignClose() {
  massSignatureStore.restoreSelection();
}

onUnmounted(() => massSignatureStore.restoreSelection());
</script>
